import React from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';

const SkillsAssessment = () => {
  const upcomingFeatures = [
    {
      icon: "Award",
      title: "Skill Certifications",
      description: "Earn verified badges and certificates for your expertise"
    },
    {
      icon: "BookOpen",
      title: "Interactive Assessments",
      description: "Take comprehensive tests across multiple skill categories"
    },
    {
      icon: "TrendingUp",
      title: "Progress Tracking",
      description: "Monitor your skill development and improvement over time"
    },
    {
      icon: "Users",
      title: "Peer Comparisons",
      description: "See how your skills stack up against other VAs"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <VAHeaderNav title="Skills Assessment" subtitle="Coming Soon - Comprehensive skill validation and certification system." />
      <Helmet>
        <title>Skills Assessment - Coming Soon - VA Sprouts</title>
        <meta name="description" content="Skills Assessment feature coming soon to VA Sprouts. Comprehensive skill validation and certification system." />
      </Helmet>
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Coming Soon Hero Section */}
          <div className="py-12 md:py-20 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6">
              <Icon name="Award" size={40} className="text-primary" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
              Skills Assessment
            </h1>
            
            <div className="inline-flex items-center px-4 py-2 bg-warning-100 rounded-full mb-6">
              <Icon name="Clock" size={20} className="text-warning mr-2" />
              <span className="text-warning font-medium">Coming Soon</span>
            </div>
            
            <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8 leading-relaxed">
              We're building a comprehensive skills assessment platform that will help you showcase your expertise, 
              earn verified certifications, and stand out to potential employers.
            </p>

            {/* Newsletter Signup */}
            <div className="bg-surface rounded-lg p-8 border border-border max-w-md mx-auto mb-12">
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                Get notified when it's ready
              </h3>
              <p className="text-text-secondary mb-4">
                Be the first to know when Skills Assessment launches.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <Button variant="primary" size="sm">
                  Notify Me
                </Button>
              </div>
            </div>
          </div>

          {/* Upcoming Features */}
          <div className="py-12">
            <h2 className="text-2xl md:text-3xl font-bold text-text-primary text-center mb-12">
              What's Coming
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {upcomingFeatures.map((feature, index) => (
                <div key={index} className="bg-surface rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mb-4">
                    <Icon name={feature.icon} size={24} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="py-12 bg-surface rounded-lg border border-border">
            <div className="px-6 md:px-8">
              <h2 className="text-2xl font-bold text-text-primary text-center mb-8">
                Development Timeline
              </h2>
              
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
                  
                  {/* Timeline items */}
                  <div className="space-y-8">
                    <div className="relative flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="CheckCircle" size={16} className="text-white" />
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-text-primary">Research & Planning</h3>
                        <p className="text-text-secondary text-sm">Completed - October 2025</p>
                      </div>
                    </div>
                    
                    <div className="relative flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-warning rounded-full flex items-center justify-center">
                        <Icon name="Clock" size={16} className="text-white" />
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-text-primary">Development Phase</h3>
                        <p className="text-text-secondary text-sm">In Progress - TBA</p>
                      </div>
                    </div>
                    
                    <div className="relative flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-border rounded-full flex items-center justify-center">
                        <Icon name="Circle" size={16} className="text-text-secondary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-text-secondary">Beta Testing</h3>
                        <p className="text-text-secondary text-sm">Planned - TBA</p>
                      </div>
                    </div>
                    
                    <div className="relative flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-border rounded-full flex items-center justify-center">
                        <Icon name="Circle" size={16} className="text-text-secondary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-text-secondary">Full Launch</h3>
                        <p className="text-text-secondary text-sm">Expected - February 2026</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="py-12 text-center">
            <h2 className="text-2xl font-bold text-text-primary mb-4">
              Ready to showcase your skills?
            </h2>
            <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
              While you wait for Skills Assessment to launch, make sure your profile is complete 
              and start connecting with employers who value your expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
              onClick={() => window.location.href = '/up-skills'}
              variant="primary" size="lg">
                Complete Your Profile
              </Button>
              <Button 
              onClick={() => window.location.href = '/job-search'}
              variant="outline" size="lg">
                Browse Jobs
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SkillsAssessment;

